import React from "react";
import incScreenBot from "../../../assets/images/incScreenBot.svg";
import userAvatar from "../../../assets/images/userAvatar.svg";

interface FollowUpConversationProps {
  /** A question's follow_up_questions. May be undefined — mandatory-criteria
   *  questions usually have no such key at all. */
  followUps?: any[];
  /** Response Analysis is only meaningful for scored questions. Position Specific
   *  questions come from the AI's req_eligibility section (score + explanation);
   *  Candidate Profile & Fit questions carry a pass/fail verdict with no score, and
   *  their follow-ups are added by hand in the admin, so there is no analysis to show.
   *  Mirrors how IncBot gates per-follow-up analysis on `!isGeneral`. */
  showAiAnalysis?: boolean;
}

/**
 * Renders a question's follow-ups as a bot/candidate chat exchange. Shared by both
 * sections of the IncScreen feedback report so they cannot drift apart again.
 */
const FollowUpConversation: React.FC<FollowUpConversationProps> = ({
  followUps,
  showAiAnalysis = true,
}) => {
  if (!Array.isArray(followUps) || followUps.length === 0) return null;

  return (
    <div className="text-[14px] leading-[25px] text-[#09090B] py-2">
      <span className="font-bold">
        Dynamic Follow ups: <br />
      </span>
      {followUps.map((fq: any, i: number) => (
        <div key={i} className="space-y-4 my-3">
          {/* Bot question - left */}
          <div className="flex gap-3 items-start mr-10">
            <img
              src={incScreenBot}
              alt="IncScreen Bot"
              className="flex-shrink-0 w-16 h-16 object-contain self-start"
            />
            <div className="relative flex-1 bg-blue-50 rounded-2xl rounded-tl-none p-4 mr-[76px]">
              <div
                className="absolute"
                style={{
                  top: 0,
                  left: "-14px",
                  width: 0,
                  height: 0,
                  borderRight: "14px solid #EFF6FF",
                  borderBottom: "14px solid transparent",
                }}
              />
              <div className="text-sm font-bold text-foreground mb-1">IncScreen</div>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {fq.question}
              </div>
            </div>
          </div>

          {/* Candidate response - right */}
          <div className="flex gap-3 items-start ml-10">
            <div className="flex-1 bg-[#F3F4F6] rounded-2xl p-4 ml-[76px]">
              {fq.expected_answer && (
                <div className="mb-3">
                  <div className="text-sm font-bold text-foreground mb-1">
                    Expected Answer
                  </div>
                  <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF]">
                    {fq.expected_answer}
                  </div>
                </div>
              )}
              <div className="mb-3">
                <div className="text-sm font-bold text-foreground mb-1">Transcript</div>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  {fq.candidate_answer}
                </div>
              </div>
              {showAiAnalysis && fq.explanation && (
                <div>
                  <div className="text-sm font-bold text-foreground mb-1">
                    Response Analysis:
                  </div>
                  <div className="p-2 text-sm text-muted-foreground leading-relaxed rounded-md bg-[#EBF4FF] border-l-4 border-[#133F7D]">
                    {fq.explanation}
                  </div>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#1B2B4B] overflow-hidden flex items-end justify-center self-start">
              <img
                src={userAvatar}
                alt="User Avatar"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FollowUpConversation;
